import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { OnboardingData } from "@/types/onboarding";
import { ArrowLeft, Loader2, Pencil, AlertCircle } from "lucide-react";

interface PreviewStepProps {
  data: OnboardingData;
  onChange: (updates: Partial<OnboardingData>) => void;
  onBack: () => void;
  onSubmit: () => void;
  onGoToStep: (step: number) => void;
  isLoading: boolean;
  error: string | null;
}

interface ReviewSectionProps {
  title: string;
  stepNumber: number;
  onEdit: (step: number) => void;
  children: React.ReactNode;
}

function ReviewSection({ title, stepNumber, onEdit, children }: ReviewSectionProps) {
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        <button
          type="button"
          onClick={() => onEdit(stepNumber)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-600 transition-colors cursor-pointer"
        >
          <Pencil className="h-3 w-3" />
          Edit
        </button>
      </div>
      <div className="p-4 space-y-2.5">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-900 font-medium text-right truncate">{value || "—"}</span>
    </div>
  );
}

const localeLabels: Record<string, string> = {
  en: "English",
  ar: "Arabic",
  es: "Spanish",
  fr: "French",
};

export default function PreviewStep({
  data,
  onChange,
  onBack,
  onSubmit,
  onGoToStep,
  isLoading,
  error,
}: PreviewStepProps) {
  const canSubmit = data.acceptTerms && !isLoading;

  return (
    <div className="space-y-5">
      {/* Library Information */}
      <ReviewSection title="Library Information" stepNumber={1} onEdit={onGoToStep}>
        <ReviewRow label="Library name" value={data.libraryName} />
        <ReviewRow
          label="Type"
          value={
            data.libraryType
              ? data.libraryType.charAt(0).toUpperCase() + data.libraryType.slice(1)
              : ""
          }
        />
        <ReviewRow
          label="URL"
          value={data.domainPrefix ? `${data.domainPrefix}.philolms.com` : ""}
        />
      </ReviewSection>

      {/* Admin Account */}
      <ReviewSection title="Admin Account" stepNumber={2} onEdit={onGoToStep}>
        <ReviewRow
          label="Name"
          value={[data.adminFirstName, data.adminLastName].filter(Boolean).join(" ")}
        />
        <ReviewRow label="Email" value={data.adminEmail} />
        <ReviewRow label="Password" value={data.adminPassword ? "••••••••" : ""} />
      </ReviewSection>

      {/* Contact & Location */}
      <ReviewSection title="Contact & Location" stepNumber={3} onEdit={onGoToStep}>
        {data.contactPhone && <ReviewRow label="Phone" value={data.contactPhone} />}
        {data.address && <ReviewRow label="Address" value={data.address} />}
        {(data.city || data.state || data.country) && (
          <ReviewRow
            label="Location"
            value={[data.city, data.state, data.country].filter(Boolean).join(", ")}
          />
        )}
        {data.postalCode && <ReviewRow label="Postal code" value={data.postalCode} />}
        <ReviewRow
          label="Language"
          value={localeLabels[data.defaultLocale] || data.defaultLocale.toUpperCase()}
        />
      </ReviewSection>

      {/* Terms */}
      <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
        <Checkbox
          id="acceptTerms"
          checked={data.acceptTerms}
          onCheckedChange={(checked) => onChange({ acceptTerms: checked === true })}
          className="mt-0.5"
        />
        <Label
          htmlFor="acceptTerms"
          className="text-sm leading-relaxed text-slate-700 cursor-pointer"
        >
          I agree to the{" "}
          <a
            href="#"
            className="text-primary hover:text-primary-600 font-medium underline-offset-2 hover:underline"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="#"
            className="text-primary hover:text-primary-600 font-medium underline-offset-2 hover:underline"
          >
            Privacy Policy
          </a>
          <span className="text-red-500 ml-0.5">*</span>
        </Label>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onBack} disabled={isLoading}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={onSubmit} disabled={!canSubmit} size="lg">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating library...
            </>
          ) : (
            "Create library"
          )}
        </Button>
      </div>
    </div>
  );
}
